import { useId, useState } from "react";

import { type LibraryAsset } from "@/api/library";
import { useCapability } from "@/auth/SessionContext";
import { lifecycleRefusal } from "@/lib/archiveRefusal";
import { newDraftId } from "@/lib/assetContent";
import {
  MODEL_FIELD_DESCRIPTION,
  PROFILE_INCOMPLETE_SUMMARY,
  SPEECH_CONFIG_ROLE,
  SPEECH_HOMOGRAPHS_ROLE,
  TARGET_OWNED_SETTINGS_NOTE,
  emptyPronunciation,
  readPronunciation,
  readSynthesisConfig,
  speechCompleteness,
  speechLanguages,
  unplaceableParts,
  writePronunciation,
  writeSpeechDocument,
  writeSynthesisConfig,
  type SpeechConfigDraft,
  type SpeechGap,
  type SpeechLanguageView,
  type SpeechPronunciationDraft,
} from "@/lib/speechContent";
import { PageHeader } from "@/shell/PageHeader";
import { Button } from "@/ui/Button";
import { Field, Input, Textarea } from "@/ui/Input";
import { Notice } from "@/ui/Notice";
import { Tooltip } from "@/ui/Tooltip";
import { SaveStatusLine } from "@/ui/library/AssetEditor";
import {
  LifecycleDialog,
  type LifecycleMode,
} from "@/ui/library/LifecycleDialog";
import { LoadingState, UnavailableState } from "@/ui/states";

import { NamedAssetMeta } from "./NamedAssetChrome";
import { NamedDocumentActions } from "./NamedDocumentEditor";
import {
  NamedConflictRecovery,
  NamedProblems,
  NamedRefusal,
} from "./NamedDraftStates";
import { NamedReadFailureState } from "./NamedReadFailure";
import { NewSpeechProfileDialog } from "./NewSpeechProfileDialog";
import { SpeechPronunciationGroups } from "./SpeechPronunciationGroups";
import { useNamedDraft, type NamedDraftEditor } from "./useNamedDraft";

/**
 * The named Speech profile editor (#870).
 *
 * ## What the canonical reference gives, and what this adds to it
 *
 * The S7 screen contract is explicit that Lovable has NO speech screen — "its
 * named Rules pages provide the reference layout, not existing speech
 * functionality" — so this surface reuses the Rules arrangement exactly and
 * states its own controls as additions rather than as a claim that a reference
 * screen exists for them.
 *
 * Reused verbatim from
 * [`/library/rules/english`](https://noproductcontent.lovable.app/library/rules/english):
 * the page header with the asset's name, the metadata strip with its Details
 * disclosure, Back and Save, and one readable prompt field for every piece of
 * prose the profile carries. Reused from
 * [`/projects/mod/config/rules`](https://noproductcontent.lovable.app/projects/mod/config/rules):
 * the compact adopted row and its exact-version adoption, which a speech profile
 * joins as its OWN pin rather than as part of the Rule.
 *
 * ADDED, and stated as additions: the three tabs (General, Gender instructions,
 * Pronunciation) and the named pronunciation groups inside the third.
 *
 * ## Why this family cannot use the generic parts form
 *
 * `AssetEditor` reaches `PartsEditor`, whose "Part key" input is the free-text
 * escape hatch the 2026-08-25 canonical amendment forbids and whose structured
 * field is a writable textarea over raw JSON. A clinician authoring MOD's
 * profile there would type `he/tts_homographs` into a text box and then
 * hand-edit a two-hundred-entry Hebrew document with combining vowel marks in
 * it. #869 shipped this family READ-ONLY for exactly that reason and recorded
 * the named editor as this ticket's; this is it.
 *
 * ## INDEPENDENT OF RULES, on the service's own terms
 *
 * `placement_registry` bounds this family's pin separately ("adopting a Rule
 * neither supplies nor consumes a speech profile") and the two place different
 * files. Nothing on this surface reads, writes or references a `language_rules`
 * asset, and the Project row is a second row rather than a field of the Rule's.
 *
 * ## WHAT STAYS OUT OF THE ASSET
 *
 * Provider activation, credentials, the Azure deployment voice, region and
 * runtime detector policy are target-owned (contract §7), and
 * `speech_profile._Document` refuses them by name at the write gate. There is
 * no control for any of them here, and the language screen says so where a
 * clinician would otherwise go looking.
 */
export function SpeechProfilePage({ asset }: { asset: LibraryAsset }) {
  const state = useNamedDraft(asset);
  const write = useCapability("library.write");
  const restore = useCapability("library.restore");
  const [openLanguage, setOpenLanguage] = useState<string | null>(null);
  const [lifecycle, setLifecycle] = useState<LifecycleMode | null>(null);
  const [copying, setCopying] = useState(false);

  const breadcrumbs = [
    { label: "Clinical library", to: "/library" },
    { label: "Rules", to: "/library/rules" },
  ];

  if (state.status === "loading") {
    return (
      <>
        <PageHeader breadcrumbs={breadcrumbs} title={asset.name} />
        <LoadingState label="Loading speech profile content" rows={4} />
      </>
    );
  }

  if (state.status === "error") {
    return (
      <>
        <PageHeader breadcrumbs={breadcrumbs} title={asset.name} />
        <NamedReadFailureState
          failure={state.failure}
          isPaused={state.isPaused}
          retry={state.retry}
          subject="Asset content"
          title="This speech profile's content could not be loaded"
          description={`The Editor Service did not return the content at ${asset.latestVersion}, so there is nothing to edit yet.`}
          deniedDescription="You are not authorized to read this speech profile's content."
          offlineDescription={`The content at ${asset.latestVersion} could not be reached, so there is nothing to edit yet. It loads on its own when the connection returns.`}
        />
      </>
    );
  }

  if (state.status === "unsupported") {
    return (
      <>
        <PageHeader breadcrumbs={breadcrumbs} title={asset.name} />
        <UnavailableState
          title="This asset's content is not a set of documents"
          description="A speech profile is authored as two per-language documents, and this asset's stored content is a different shape."
        />
      </>
    );
  }

  const { editor } = state;
  const languages = speechLanguages(editor.parts);
  const open =
    openLanguage === null
      ? null
      : (languages.find((entry) => entry.language === openLanguage) ?? null);

  if (open) {
    return (
      <SpeechLanguageScreen
        asset={asset}
        editor={editor}
        view={open}
        breadcrumbs={breadcrumbs}
        onBack={() => setOpenLanguage(null)}
      />
    );
  }

  const archiveMode: LifecycleMode = asset.capabilities.archived
    ? "unarchive"
    : "archive";
  // Direction-specific caller gate (#647), the same split the service enforces:
  // archiving is `library.write`, restoring is the reserved `library.restore`.
  const archiveRefusal = lifecycleRefusal(
    asset,
    archiveMode === "unarchive" ? restore.reason : write.reason,
  );
  const archiveLabel = archiveMode === "archive" ? "Archive" : "Unarchive";
  const unplaceable = unplaceableParts(editor.parts);

  return (
    <>
      <PageHeader
        breadcrumbs={breadcrumbs}
        title={asset.name}
        description={`${languages.length} ${languages.length === 1 ? "language" : "languages"}`}
        meta={
          <NamedAssetMeta
            asset={asset}
            version={editor.pinned}
            identity={`speech/${asset.id}`}
          />
        }
        actions={
          <>
            {/*
              GATED ON THE CAPABILITY THE ACTION ACTUALLY NEEDS. Both labels
              open a dialog whose only outcome is `POST /v1/library/assets`,
              which the service refuses without `library.write` — so an
              unauthorized clinician could fill in eight fields and meet a 403
              at the end. The refusal is stated on the control instead, which is
              the same shape `lifecycleRefusal` gives the button beside it.
            */}
            <Tooltip
              content={
                write.reason ??
                (asset.capabilities.seeded
                  ? "Part of the base clinical library. The original stays as it is — make an editable copy to create your own version."
                  : `Start a new speech profile from ${asset.name}`)
              }
            >
              <Button
                variant="outline"
                disabled={write.reason !== null}
                onClick={() => setCopying(true)}
              >
                {asset.capabilities.seeded
                  ? "Create editable copy"
                  : "Duplicate"}
              </Button>
            </Tooltip>
            <Tooltip content={archiveRefusal ?? `${archiveLabel} this asset`}>
              <Button
                variant="outline"
                disabled={archiveRefusal !== null}
                onClick={() => setLifecycle(archiveMode)}
              >
                {archiveLabel}
              </Button>
            </Tooltip>
          </>
        }
      />

      <div className="space-y-4">
        <NamedConflictRecovery editor={editor} />
        <NamedRefusal editor={editor} />
        <NamedProblems editor={editor} />

        {unplaceable.length > 0 ? (
          // `speech_profile._require_language` refuses these at Save, and the
          // remedy is not a control this editor offers — deliberately, because
          // the control that would fix it is the free-text part key this family
          // exists to remove. So the surface states the fact and names what has
          // to change, rather than presenting a document it cannot place.
          <Notice tone="warning">
            <p>
              {unplaceable.length}{" "}
              {unplaceable.length === 1 ? "document" : "documents"} in this
              profile {unplaceable.length === 1 ? "does" : "do"} not name a
              language. Every file a speech profile writes is per-language, so
              this profile cannot be saved until they do — and this editor does
              not author document keys, so they have to be replaced through the
              Editor Service.
            </p>
          </Notice>
        ) : null}

        {languages.length === 0 ? (
          <UnavailableState
            title="This profile carries no language yet"
            description="A speech profile holds one synthesis configuration and one set of pronunciation tables per language, and this asset's stored content names none."
          />
        ) : (
          <ol
            aria-label="Languages this speech profile carries"
            className="overflow-hidden rounded-md border border-border bg-card"
          >
            {languages.map((view) => (
              <li
                key={view.language}
                className="flex flex-wrap items-center gap-2 border-b border-border px-3 py-2 last:border-b-0"
              >
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm text-foreground">
                    {view.label}
                  </span>
                  <span className="block text-xs text-muted-foreground">
                    {describeLanguage(view)}
                  </span>
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setOpenLanguage(view.language)}
                >
                  Open
                </Button>
              </li>
            ))}
          </ol>
        )}

        {editor.readOnly ? null : (
          <div className="flex flex-wrap items-center justify-end gap-3">
            <SaveStatusLine
              status={
                editor.saving
                  ? "saving"
                  : editor.failure !== null
                    ? "error"
                    : editor.dirty
                      ? "dirty"
                      : editor.lastSavedAt
                        ? "saved"
                        : "clean"
              }
              lastSavedAt={editor.lastSavedAt}
            />
            <Button
              variant="outline"
              disabled={!editor.dirty || editor.saving}
              onClick={editor.discard}
            >
              Discard
            </Button>
            <Button
              disabled={!editor.dirty || editor.saving}
              onClick={() => editor.save()}
            >
              Save
            </Button>
          </div>
        )}
      </div>

      {lifecycle ? (
        <LifecycleDialog
          asset={asset}
          mode={lifecycle}
          onClose={() => setLifecycle(null)}
        />
      ) : null}
      {copying ? (
        <NewSpeechProfileDialog
          copyFrom={asset}
          onClose={() => setCopying(false)}
        />
      ) : null}
    </>
  );
}

/** What one language row holds, without naming a role vocabulary. */
function describeLanguage(view: SpeechLanguageView): string {
  const held: string[] = [];
  if (view.config !== null) {
    held.push("synthesis settings");
  }
  if (view.pronunciation !== null) {
    held.push("pronunciation tables");
  }
  if (view.other.length > 0) {
    held.push(
      `${view.other.length} other ${view.other.length === 1 ? "document" : "documents"}`,
    );
  }
  return held.length === 0 ? "No content yet" : held.join(" · ");
}

const TABS = [
  { id: "general", label: "General" },
  { id: "gender", label: "Gender instructions" },
  { id: "pronunciation", label: "Pronunciation" },
] as const;

type TabId = (typeof TABS)[number]["id"];

/**
 * One language, open: the canonical named content screen, with three tabs.
 *
 * THE HEADER IS THE REFERENCE'S — heading, metadata strip with Details, Back and
 * Save — and the three tabs are this ticket's addition. They exist because the
 * profile is genuinely three different kinds of authoring: typed synthesis
 * parameters, two readable prose instructions, and word tables. One flat screen
 * would put a two-hundred-row Hebrew table between the model name and the
 * instructions a clinician reads together.
 */
function SpeechLanguageScreen({
  asset,
  editor,
  view,
  breadcrumbs,
  onBack,
}: {
  asset: LibraryAsset;
  editor: NamedDraftEditor;
  view: SpeechLanguageView;
  breadcrumbs: { label: string; to?: string }[];
  onBack: () => void;
}) {
  const [tab, setTab] = useState<TabId>("general");
  const tablistId = useId();

  const config = readSynthesisConfig(view.config);
  const pronunciation = readPronunciation(view.pronunciation);
  const gaps = speechCompleteness(config, pronunciation);

  function changeConfig(next: SpeechConfigDraft) {
    editor.change(
      writeSpeechDocument(
        editor.parts,
        view.language,
        SPEECH_CONFIG_ROLE,
        writeSynthesisConfig(next),
        newDraftId,
      ),
    );
  }

  function changePronunciation(next: SpeechPronunciationDraft) {
    editor.change(
      writeSpeechDocument(
        editor.parts,
        view.language,
        SPEECH_HOMOGRAPHS_ROLE,
        writePronunciation(next),
        newDraftId,
      ),
    );
  }

  return (
    <>
      <PageHeader
        breadcrumbs={[
          ...breadcrumbs,
          { label: asset.name, to: `/library/rules/${asset.id}` },
          { label: view.label },
        ]}
        title={view.label}
        description="Speech profile"
        meta={
          <NamedAssetMeta
            asset={asset}
            version={editor.pinned}
            identity={`speech/${asset.id}`}
          />
        }
        actions={
          <NamedDocumentActions
            readOnly={editor.readOnly}
            dirty={editor.dirty}
            saving={editor.saving}
            onBack={onBack}
            onSave={() => editor.save()}
          />
        }
      />

      <div className="space-y-4">
        <NamedConflictRecovery editor={editor} />
        <NamedRefusal editor={editor} />
        <NamedProblems editor={editor} />

        {editor.readOnly && editor.readOnlyReason ? (
          <p className="rounded-md border border-border bg-muted px-3 py-2 text-sm text-muted-foreground">
            {editor.readOnlyReason}
          </p>
        ) : null}

        <CompletenessNotice gaps={gaps} onOpen={setTab} />

        <div
          role="tablist"
          id={tablistId}
          aria-label="Speech profile settings"
          className="flex flex-wrap gap-1 border-b border-border"
        >
          {TABS.map((entry) => (
            <button
              key={entry.id}
              type="button"
              role="tab"
              id={`${tablistId}-${entry.id}`}
              aria-selected={tab === entry.id}
              aria-controls={`${tablistId}-${entry.id}-panel`}
              // Roving tabindex: only the selected tab is in the tab order, and
              // the arrow keys move between them — the ARIA authoring practice
              // for a tab list, and what keeps a three-tab strip from adding
              // three stops to every keyboard traversal of the page.
              tabIndex={tab === entry.id ? 0 : -1}
              className={`-mb-px border-b-2 px-3 py-2 text-sm ${
                tab === entry.id
                  ? "border-primary font-medium text-foreground"
                  : "border-transparent text-muted-foreground hover:text-foreground"
              }`}
              onClick={() => setTab(entry.id)}
              onKeyDown={(event) => {
                if (event.key !== "ArrowRight" && event.key !== "ArrowLeft") {
                  return;
                }
                event.preventDefault();
                const index = TABS.findIndex((item) => item.id === tab);
                const next =
                  event.key === "ArrowRight"
                    ? (index + 1) % TABS.length
                    : (index - 1 + TABS.length) % TABS.length;
                const target = TABS[next];
                if (target) {
                  setTab(target.id);
                  document
                    .getElementById(`${tablistId}-${target.id}`)
                    ?.focus();
                }
              }}
            >
              {entry.label}
            </button>
          ))}
        </div>

        {TABS.map((entry) => (
          <div
            key={entry.id}
            role="tabpanel"
            id={`${tablistId}-${entry.id}-panel`}
            aria-labelledby={`${tablistId}-${entry.id}`}
            // `hidden` rather than unmounting: an unsaved edit on one tab must
            // survive a look at another, and the draft lives in `useNamedDraft`
            // while the scroll position, filters and add-row state of a table
            // do not.
            hidden={tab !== entry.id}
            tabIndex={0}
          >
            {entry.id === "general" ? (
              <GeneralTab
                read={config}
                readOnly={editor.readOnly}
                onChange={changeConfig}
              />
            ) : entry.id === "gender" ? (
              <GenderInstructionsTab
                read={config}
                readOnly={editor.readOnly}
                onChange={changeConfig}
              />
            ) : (
              <PronunciationTab
                read={pronunciation}
                readOnly={editor.readOnly}
                onChange={changePronunciation}
              />
            )}
          </div>
        ))}
      </div>
    </>
  );
}

/**
 * What this language has not written yet, with a way to reach each control.
 *
 * NOT A GATE. The service refuses an incomplete profile with a field pointer at
 * both author call sites, and the engine refuses the same shapes at a
 * candidate's provider build; this states the requirement beside the controls so
 * a clinician meets it while authoring. It is also the answer to "target
 * defaults cannot hide missing fields": a profile that authors both files
 * DECLINES the deployment target's parents, so an unwritten field is not a field
 * something underneath supplies — it is a field the candidate will be refused
 * for.
 */
function CompletenessNotice({
  gaps,
  onOpen,
}: {
  gaps: readonly SpeechGap[];
  onOpen: (tab: TabId) => void;
}) {
  if (gaps.length === 0) {
    return null;
  }
  const groups = TABS.filter((tab) => gaps.some((gap) => gap.group === tab.id));

  return (
    <Notice tone="warning">
      <p>
        {`This profile is not complete yet. ${PROFILE_INCOMPLETE_SUMMARY} — the Editor Service refuses to save it until every field is written.`}
      </p>
      <ul className="mt-1 list-inside list-disc">
        {gaps.map((gap) => (
          <li key={`${gap.group}:${gap.label}`}>{gap.label}</li>
        ))}
      </ul>
      <div className="mt-2 flex flex-wrap gap-2">
        {groups.map((tab) => (
          <Button
            key={tab.id}
            variant="outline"
            size="sm"
            onClick={() => onOpen(tab.id)}
          >
            Go to {tab.label}
          </Button>
        ))}
      </div>
    </Notice>
  );
}

/** The shared refusal for a document this editor will not re-serialise. */
function UnreadableDocument({ subject }: { subject: string }) {
  return (
    <UnavailableState
      title={`This profile's ${subject} cannot be edited here`}
      description={
        <>
          <p>
            The stored document holds something this editor does not recognise,
            and every edit here rewrites the whole document — so editing it would
            publish an immutable version with that content missing.
          </p>
          <p className="mt-2">
            The document itself is unchanged and still part of this asset.
            Nothing on this screen alters it.
          </p>
        </>
      }
    />
  );
}

function GeneralTab({
  read,
  readOnly,
  onChange,
}: {
  read: ReturnType<typeof readSynthesisConfig>;
  readOnly: boolean;
  onChange: (next: SpeechConfigDraft) => void;
}) {
  if (read.kind === "unreadable") {
    return <UnreadableDocument subject="synthesis settings" />;
  }
  const draft: SpeechConfigDraft =
    read.kind === "ready"
      ? read.draft
      : {
          model: "",
          voice: "",
          languageCode: "",
          temperature: "",
          instructions: "",
          female: "",
          male: "",
        };

  return (
    <div className="space-y-4 pt-4">
      <div className="grid gap-4 sm:grid-cols-2">
        <Field
          label="Model"
          description={MODEL_FIELD_DESCRIPTION}
        >
          {({ inputId, describedBy }) => (
            <Input
              id={inputId}
              dir="ltr"
              aria-describedby={describedBy}
              value={draft.model}
              readOnly={readOnly}
              aria-disabled={readOnly || undefined}
              onChange={(event) =>
                onChange({ ...draft, model: event.target.value })
              }
            />
          )}
        </Field>
        <Field label="Voice" description="The voice name the model synthesizes with.">
          {({ inputId, describedBy }) => (
            <Input
              id={inputId}
              dir="ltr"
              aria-describedby={describedBy}
              value={draft.voice}
              readOnly={readOnly}
              aria-disabled={readOnly || undefined}
              onChange={(event) =>
                onChange({ ...draft, voice: event.target.value })
              }
            />
          )}
        </Field>
        <Field
          label="Language code"
          description="The spoken language and region, as the model names it — for example he-IL."
        >
          {({ inputId, describedBy }) => (
            <Input
              id={inputId}
              dir="ltr"
              aria-describedby={describedBy}
              value={draft.languageCode}
              readOnly={readOnly}
              aria-disabled={readOnly || undefined}
              onChange={(event) =>
                onChange({ ...draft, languageCode: event.target.value })
              }
            />
          )}
        </Field>
        <Field
          label="Temperature"
          // ZERO IS A VALUE AND HAS TO SURVIVE. The source packs set it to zero
          // deliberately — no sampling variation — and the service refuses the
          // float spelling `0.0` because JavaScript writes it `0` and the two
          // runtimes would hash one document two ways. So the description asks
          // for the whole number, and the buffer travels verbatim.
          description="How much the voice varies between renderings. Write a whole number without a decimal point: 0 means no variation."
        >
          {({ inputId, describedBy }) => (
            <Input
              id={inputId}
              dir="ltr"
              inputMode="decimal"
              aria-describedby={describedBy}
              value={draft.temperature}
              readOnly={readOnly}
              aria-disabled={readOnly || undefined}
              onChange={(event) =>
                onChange({ ...draft, temperature: event.target.value })
              }
            />
          )}
        </Field>
      </div>

      <Field
        label="Speaking instructions"
        description="Sent to the model with every turn, exactly as written here. The instruction for the patient's recorded gender is added to it."
      >
        {({ inputId, describedBy }) => (
          <Textarea
            id={inputId}
            dir="auto"
            rows={6}
            aria-describedby={describedBy}
            value={draft.instructions}
            readOnly={readOnly}
            onChange={(event) =>
              onChange({ ...draft, instructions: event.target.value })
            }
          />
        )}
      </Field>

      {/*
        THE BOUNDARY, ON THE SCREEN. Contract §7 keeps provider activation,
        credentials, the deployment voice, region and detector policy
        target-owned, and `speech_profile._Document` refuses them by name at the
        write gate. A clinician who came here to switch a provider on needs that
        sentence, not a screen where the control they expected is silently
        absent. The MODEL joined that list with liv-brain#316 / liv1.0#1165 —
        a profile may still carry the key (`MODEL_FIELD_DESCRIPTION`) but it no
        longer chooses it — so the sentence names it beside the rest.
      */}
      <p className="text-sm text-muted-foreground">{TARGET_OWNED_SETTINGS_NOTE}</p>
    </div>
  );
}

function GenderInstructionsTab({
  read,
  readOnly,
  onChange,
}: {
  read: ReturnType<typeof readSynthesisConfig>;
  readOnly: boolean;
  onChange: (next: SpeechConfigDraft) => void;
}) {
  if (read.kind === "unreadable") {
    return <UnreadableDocument subject="synthesis settings" />;
  }
  const draft: SpeechConfigDraft =
    read.kind === "ready"
      ? read.draft
      : {
          model: "",
          voice: "",
          languageCode: "",
          temperature: "",
          instructions: "",
          female: "",
          male: "",
        };

  return (
    <div className="space-y-4 pt-4">
      <p className="text-sm text-muted-foreground">
        Added to the speaking instructions for a patient whose gender is
        recorded. A conversation with no recorded gender gets the speaking
        instructions on their own, so neither of these is a fallback for the
        other.
      </p>

      <Field
        label="Instruction for a female listener"
        description="Written out in full, exactly as the model receives it."
      >
        {({ inputId, describedBy }) => (
          <Textarea
            id={inputId}
            dir="auto"
            rows={6}
            aria-describedby={describedBy}
            value={draft.female}
            readOnly={readOnly}
            onChange={(event) =>
              onChange({ ...draft, female: event.target.value })
            }
          />
        )}
      </Field>

      <Field
        label="Instruction for a male listener"
        description="Written out in full, exactly as the model receives it."
      >
        {({ inputId, describedBy }) => (
          <Textarea
            id={inputId}
            dir="auto"
            rows={6}
            aria-describedby={describedBy}
            value={draft.male}
            readOnly={readOnly}
            onChange={(event) => onChange({ ...draft, male: event.target.value })}
          />
        )}
      </Field>
    </div>
  );
}

function PronunciationTab({
  read,
  readOnly,
  onChange,
}: {
  read: ReturnType<typeof readPronunciation>;
  readOnly: boolean;
  onChange: (next: SpeechPronunciationDraft) => void;
}) {
  if (read.kind === "unreadable") {
    return <UnreadableDocument subject="pronunciation tables" />;
  }
  const draft =
    read.kind === "ready" ? read.draft : emptyPronunciation();

  return (
    <div className="pt-4">
      <SpeechPronunciationGroups
        draft={draft}
        readOnly={readOnly}
        onChange={onChange}
      />
    </div>
  );
}
